/**
 * <dd-syndicate-articles> — Circle Community Lounge + Shopify blog posts.
 *
 * List:  /pages/syndicate-articles
 * Detail: /pages/syndicate-articles?slug=<circle-slug>
 *
 * Gating (server-enforced via Shopify App Proxy + Admin API):
 *   Full body + comments only when the signed proxy request maps to a
 *   customer with orders_count >= syndicate threshold. UI paywall mirrors
 *   server-truncated preview HTML for ineligible visitors.
 */
if (!customElements.get('dd-syndicate-articles')) {
  const LIST_CACHE_KEY = 'dd-sa-posts-v4';
  const HLS_SCRIPT_SRC = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.min.js';
  const LIST_CACHE_TTL_MS = 5 * 60 * 1000;

  const DD_SA_LUCIDE = {
    lock: '<svg class="dd-lucide" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
    'chevron-left': '<svg class="dd-lucide" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
    'chevron-right': '<svg class="dd-lucide" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
  };

  class DDSyndicateArticles extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.apiUrl = (this.dataset.apiUrl || '').replace(/\/$/, '');
      this.syndicatePageUrl = this.dataset.syndicatePageUrl || '/pages/syndicate';
      this.articlesPageUrl = this.dataset.articlesPageUrl || '/pages/syndicate-articles';
      this.limit = Number(this.dataset.limit || 0) || 0;
      this.mode = this.dataset.mode || 'auto';
      this.eligible = document.documentElement.dataset.syndicateEligible === 'yes';
      this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
      this.customerName = this.dataset.customerName || '';
      this.customerEmail = this.dataset.customerEmail || '';

      this.activeTopic = 'all';
      this.posts = [];
      this.topics = [];
      this._commentsPostId = null;
      this._isCommentPosting = false;
      this._skeletonVisible = false;
      this._titleSuffix = this._readTitleSuffix();

      this._bindEvents();
      if (this.limit > 0) {
        this._onFeedResize = () => this._renderGrid();
        window.addEventListener('resize', this._onFeedResize, { passive: true });
      }
      this._boot();
    }

    disconnectedCallback() {
      if (this._onFeedResize) {
        window.removeEventListener('resize', this._onFeedResize);
        this._onFeedResize = null;
      }
    }

    _bindEvents() {
      this.addEventListener('click', (e) => {
        const filter = e.target.closest('[data-dd-sa-filter]');
        if (filter) {
          e.preventDefault();
          this.activeTopic = filter.getAttribute('data-dd-sa-filter') || 'all';
          this._renderFilters();
          this._renderGrid();
          return;
        }

      });

      const form = this.querySelector('[data-dd-sa-comment-form]');
      if (form) {
        form.addEventListener('submit', (e) => this._onCommentSubmit(e));
      }
    }

    _shopifyPosts() {
      const el = this.querySelector('[data-dd-sa-shopify-posts]');
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_err) {
        return [];
      }
    }

    _mergePosts(circlePosts, circleTopics) {
      const shopify = this._shopifyPosts();
      const seen = new Set();
      const posts = [];
      [...shopify, ...(circlePosts || [])].forEach((p) => {
        if (!p) return;
        const key = p.source === 'shopify' ? `s:${p.id}` : `c:${p.slug || p.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        posts.push(p);
      });
      posts.sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));

      const topicMap = new Map();
      (circleTopics || []).forEach((t) => {
        const name = typeof t === 'string' ? t : t && t.name;
        if (!name) return;
        topicMap.set(name.toLowerCase(), { name });
      });
      shopify.forEach((p) => {
        (p.topics || []).forEach((name) => {
          if (!name) return;
          const key = String(name).toLowerCase();
          if (!topicMap.has(key)) topicMap.set(key, { name });
        });
      });
      return { posts, topics: Array.from(topicMap.values()) };
    }

    async _boot() {
      if (!this.apiUrl && this._shopifyPosts().length === 0) {
        this._setStatus('Articles API is not configured.');
        return;
      }

      const slug = this._currentSlug();
      const isDetail = this.mode === 'detail' || (this.mode === 'auto' && slug);

      if (isDetail && slug) {
        const shopifyMatch = this._shopifyPosts().find((p) => p && p.slug === slug);
        if (shopifyMatch && shopifyMatch.url) {
          window.location.replace(shopifyMatch.url);
          return;
        }
        this.classList.add('dd-syndicate-articles--detail');
        this.closest('.dd-syndicate-articles')?.classList.add('is-detail');
        document.querySelector('.dd-page-intro')?.setAttribute('hidden', '');
        await this._loadDetail(slug);
        return;
      }

      const topicParam = this._currentTopic();
      if (topicParam) this.activeTopic = topicParam;

      await this._loadList();
    }

    _currentSlug() {
      try {
        return new URL(window.location.href).searchParams.get('slug') || '';
      } catch (_err) {
        return '';
      }
    }

    _currentTopic() {
      try {
        return (new URL(window.location.href).searchParams.get('topic') || '').trim().toLowerCase();
      } catch (_err) {
        return '';
      }
    }

    _apiFetch(url, options = {}) {
      return fetch(url, {
        credentials: 'same-origin',
        ...options,
      });
    }

    async _loadList() {
      const cached = this._readListCache();
      if (cached) {
        const merged = this._mergePosts(cached.posts || [], cached.topics || []);
        this.posts = merged.posts;
        this.topics = merged.topics;
        this._renderFilters();
        this._renderGrid();
        this._updateListSeo();
        this.classList.add('is-revalidating');
      } else {
        this._showListSkeleton();
      }

      this._setBusy(true);
      try {
        if (!this.apiUrl) {
          const merged = this._mergePosts([], []);
          this.posts = merged.posts;
          this.topics = merged.topics;
          this._renderFilters();
          this._renderGrid();
          this._updateListSeo();
          this._clearStatus();
          return;
        }
        const res = await this._apiFetch(`${this.apiUrl}/posts`);
        if (!res.ok) throw new Error('Failed to load articles');
        const data = await res.json();
        this._writeListCache(data);
        const merged = this._mergePosts(data.posts || [], data.topics || []);
        this.posts = merged.posts;
        this.topics = merged.topics;
        this._renderFilters();
        this._renderGrid();
        this._updateListSeo();
        this._clearStatus();
      } catch (err) {
        const shopifyOnly = this._mergePosts((cached && cached.posts) || [], (cached && cached.topics) || []);
        if (shopifyOnly.posts.length) {
          this.posts = shopifyOnly.posts;
          this.topics = shopifyOnly.topics;
          this._renderFilters();
          this._renderGrid();
          this._updateListSeo();
          this._setStatus('Could not refresh community articles. Showing available posts.');
        } else if (!cached) {
          this._showListError('Could not load community articles right now.');
        } else {
          this._setStatus('Could not refresh articles. Showing cached posts.');
        }
        console.error('[dd-syndicate-articles]', err);
      } finally {
        this.classList.remove('is-revalidating');
        this._skeletonVisible = false;
        this._setBusy(false);
      }
    }

    async _loadDetail(slug) {
      this._showDetailSkeleton();
      this._setBusy(true);
      try {
        const detailUrl = `${this.apiUrl}/posts?slug=${encodeURIComponent(slug)}`;
        const res = await this._apiFetch(detailUrl);
        if (!res.ok) throw new Error('Article not found');
        const data = await res.json();
        const access = data.access || data.post?.access || 'preview';
        const fullAccess = access === 'full';
        this._renderDetail(data.post, data.related || [], data.prev_post, data.next_post, fullAccess);
        this._updateDetailSeo(data.post, fullAccess);
        if (fullAccess && data.post && data.post.id) {
          if (data.comments && Array.isArray(data.comments.comments)) {
            this._commentsPostId = data.post.id;
            this._renderComments(data.comments.comments);
          } else {
            await this._loadComments(data.post.id);
          }
        }
        this._clearStatus();
      } catch (err) {
        this._showDetailError('Could not load this article.');
        console.error('[dd-syndicate-articles]', err);
      } finally {
        this._skeletonVisible = false;
        this._setBusy(false);
      }
    }

    async _loadComments(postId) {
      const list = this.querySelector('[data-dd-sa-comments-list]');
      if (!list) return;
      if (this._commentsPostId === postId && list.childElementCount > 0) return;

      try {
        const res = await this._apiFetch(`${this.apiUrl}/comments?post_id=${postId}`);
        if (!res.ok) return;
        const data = await res.json();
        this._commentsPostId = postId;
        this._renderComments(data.comments || []);
      } catch (err) {
        console.error('[dd-syndicate-articles] comments', err);
      }
    }

    _renderComments(comments) {
      const list = this.querySelector('[data-dd-sa-comments-list]');
      if (!list) return;

      if (!comments.length) {
        list.innerHTML = '<p class="dd-syndicate-articles__empty">No comments yet.</p>';
        return;
      }

      list.innerHTML = comments.map((c) => this._commentHtml(c)).join('');
      this._bindCommentAvatarFallbacks(list);
    }

    _commentInitials(name) {
      return String(name || 'Member')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase() || 'M';
    }

    _bindCommentAvatarFallbacks(list) {
      list.querySelectorAll('img.dd-syndicate-articles__comment-avatar').forEach((img) => {
        img.addEventListener(
          'error',
          () => {
            const meta =
              img.closest('.dd-syndicate-articles__comment')?.querySelector(
                '.dd-syndicate-articles__comment-meta'
              )?.textContent || '';
            const name = meta.split('·')[0].trim() || 'Member';
            const fallback = document.createElement('span');
            fallback.className =
              'dd-syndicate-articles__comment-avatar dd-syndicate-articles__comment-avatar--fallback';
            fallback.setAttribute('aria-hidden', 'true');
            fallback.textContent = this._commentInitials(name);
            img.replaceWith(fallback);
          },
          { once: true }
        );
      });
    }

    _commentAvatarHtml(comment) {
      const name = comment.author || 'Member';
      const avatarUrl = comment.author_avatar_url;
      if (avatarUrl) {
        return `<img class="dd-syndicate-articles__comment-avatar" src="${this._escapeUrlAttr(avatarUrl)}" alt="" width="40" height="40" loading="lazy" decoding="async">`;
      }

      return `<span class="dd-syndicate-articles__comment-avatar dd-syndicate-articles__comment-avatar--fallback" aria-hidden="true">${this._escape(this._commentInitials(name))}</span>`;
    }

    _commentHtml(comment) {
      return `
        <article class="dd-syndicate-articles__comment">
          <div class="dd-syndicate-articles__comment-head">
            ${this._commentAvatarHtml(comment)}
            <div class="dd-syndicate-articles__comment-meta">${this._escape(comment.author || 'Member')} · ${this._formatDate(comment.created_at)}</div>
          </div>
          <div class="dd-syndicate-articles__comment-body">${this._sanitizeCircleHtml(comment.body_html || '')}</div>
        </article>`;
    }

    async _onCommentSubmit(e) {
      e.preventDefault();
      if (this._isCommentPosting) return;
      if (!this.eligible || !this.customerLoggedIn || !this.customerEmail) {
        window.location.href = this.syndicatePageUrl;
        return;
      }

      const form = e.target;
      const postId = Number(form.getAttribute('data-post-id'));
      const body = form.querySelector('[name="body"]')?.value?.trim();
      const name = form.querySelector('[name="name"]')?.value?.trim() || this.customerName;
      const email = form.querySelector('[name="email"]')?.value?.trim() || this.customerEmail;
      const status = form.querySelector('[data-dd-sa-form-status]');
      const submit = form.querySelector('[type="submit"]');

      if (!postId || !body) return;

      this._isCommentPosting = true;
      submit.disabled = true;
      if (status) status.textContent = 'Posting…';

      try {
        const res = await this._apiFetch(`${this.apiUrl}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ post_id: postId, body, name }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Comment failed');
        form.reset();
        if (status) status.textContent = 'Comment posted to the community.';
        this._commentsPostId = null;
        await this._loadComments(postId);
      } catch (err) {
        if (status) status.textContent = err.message || 'Could not post comment.';
        console.error('[dd-syndicate-articles] comment', err);
      } finally {
        this._isCommentPosting = false;
        submit.disabled = false;
      }
    }

    _renderFilters() {
      const host = this.querySelector('[data-dd-sa-filters]');
      if (!host || this.limit > 0) return;

      const pills = [{ name: 'All posts', value: 'all' }].concat(
        this.topics.map((t) => ({ name: t.name, value: t.name.toLowerCase() }))
      );

      const html = pills
        .map(
          (p) => `
            <button
              type="button"
              class="dd-syndicate-articles__filter${this.activeTopic === p.value ? ' is-active' : ''}"
              data-dd-sa-filter="${this._escapeAttr(p.value)}"
            >${this._escape(p.name)}</button>`
        )
        .join('');

      this._revealHost(host, html, { kind: 'filters' });
    }

    _renderGrid() {
      const host = this.querySelector('[data-dd-sa-grid]');
      if (!host) return;

      let items = this.posts.slice();
      if (this.activeTopic !== 'all') {
        items = items.filter((p) =>
          (p.topics || []).some((t) => t.toLowerCase() === this.activeTopic)
        );
      }
      if (this.limit > 0) items = items.slice(0, this._feedDisplayLimit());

      if (!items.length) {
        host.innerHTML = '<p class="dd-syndicate-articles__empty">No articles match this filter yet.</p>';
        return;
      }

      const gridClass =
        this.limit > 0
          ? 'dd-syndicate-articles__grid dd-syndicate-articles__grid--feed'
          : 'dd-syndicate-articles__grid';

      this._revealHost(host, `<div class="${gridClass}">${items.map((p) => this._cardHtml(p)).join('')}</div>`, {
        kind: 'grid',
      });
    }

    _detailHeroHtml(post, fullAccess) {
      const topic = (post.topics && post.topics[0]) || '';
      const meta = `${this._formatDate(post.published_at)} · ${this._escape(post.author || '')}`;
      const topicHeroHtml = topic
        ? `<span class="dd-syndicate-articles__topic-pill dd-syndicate-articles__topic-pill--hero">${this._escape(topic)}</span>`
        : '';
      const videoPlayerVisible = post.has_video && fullAccess;

      if (post.image_url && !videoPlayerVisible) {
        return `
          <header class="dd-syndicate-articles__article-hero">
            <div class="dd-syndicate-articles__article-hero-media">
              <img src="${this._escapeUrlAttr(post.image_url)}" alt="" loading="eager" fetchpriority="high" decoding="async">
              <div class="dd-syndicate-articles__article-hero-scrim" aria-hidden="true"></div>
            </div>
            <div class="dd-syndicate-articles__article-hero-content">
              ${topicHeroHtml}
              <h1 class="dd-syndicate-articles__article-title">${this._escape(post.title)}</h1>
              <p class="dd-syndicate-articles__article-meta">${meta}</p>
              ${this._authorTagsHtml(post)}
            </div>
          </header>`;
      }

      return `
        <header class="dd-syndicate-articles__article-header">
          ${topic ? `<div class="dd-syndicate-articles__topic-pill">${this._escape(topic)}</div>` : ''}
          <h1 class="dd-syndicate-articles__article-title">${this._escape(post.title)}</h1>
          <p class="dd-syndicate-articles__article-meta">${meta}</p>
          ${this._authorTagsHtml(post)}
        </header>`;
    }

    _renderDetail(post, related, prevPost, nextPost, fullAccess) {
      const host = this.querySelector('[data-dd-sa-detail]');
      if (!host || !post) return;

      const locked =
        post.source !== 'shopify' &&
        post.access !== 'full' &&
        fullAccess !== true;

      const html = `
        <nav class="dd-syndicate-articles__breadcrumb" aria-label="Breadcrumb">
          <a href="/">Home</a> / <a href="${this._escapeAttr(this.articlesPageUrl)}">Syndicate Articles</a> / <span>${this._escape(post.title)}</span>
        </nav>
        ${this._detailHeroHtml(post, !locked)}
        <div class="dd-syndicate-articles__content${locked ? ' is-locked' : ''}">
          <div class="dd-syndicate-articles__content-inner dd-sa-prose">${this._prepareBodyHtml(post.body_html, post.image_url)}</div>
          ${locked ? this._paywallHtml() : ''}
        </div>
        ${this._authorCardHtml(post)}
      `;

      this._revealHost(host, html, { kind: 'detail' });
      this._hydrateCircleVideos(host);
      this._bindAuthorAvatarFallback(host, post);

      /* Tags + prev/next live BELOW the comment form: reading order is
         article -> author signature -> leave a comment -> onward nav. */
      const postNavHost = this.querySelector('[data-dd-sa-postnav]');
      if (postNavHost) {
        postNavHost.innerHTML = this._postFooterHtml(post, prevPost, nextPost);
      }

      const relatedHost = this.querySelector('[data-dd-sa-related]');
      if (relatedHost) {
        relatedHost.innerHTML =
          related.length > 0
            ? `
          <h2 class="dd-syndicate-articles__related-heading">Related posts</h2>
          <div class="dd-syndicate-articles__grid dd-syndicate-articles__grid--3">${related.map((p) => this._cardHtml(p)).join('')}</div>
        `
            : '';
      }

      const form = this.querySelector('[data-dd-sa-comment-form]');
      if (form) {
        form.setAttribute('data-post-id', String(post.id));
        form.hidden = locked;
      }
    }

    /* End-of-article signature: signals the read is over before the
       tags/nav footer and the comment form below. */
    _authorCardHtml(post) {
      if (!post || !post.author) return '';

      const initial = (post.author || '?').trim().charAt(0).toUpperCase();
      const avatar = post.author_avatar_url
        ? `<img class="dd-syndicate-articles__author-avatar" src="${this._escapeUrlAttr(post.author_avatar_url)}" alt="" width="64" height="64" loading="lazy" decoding="async">`
        : `<span class="dd-syndicate-articles__author-avatar dd-syndicate-articles__author-avatar--fallback" aria-hidden="true">${this._escape(initial)}</span>`;

      const headline = post.author_headline
        ? `<p class="dd-syndicate-articles__author-headline">${this._escape(post.author_headline)}</p>`
        : '';

      return `
        <aside class="dd-syndicate-articles__author-card" aria-label="About the author">
          ${avatar}
          <div class="dd-syndicate-articles__author-info">
            <p class="dd-syndicate-articles__author-label">Written by</p>
            <p class="dd-syndicate-articles__author-name">${this._escape(post.author)}</p>
            ${headline}
            ${this._authorTagsHtml(post)}
          </div>
        </aside>`;
    }

    _bindAuthorAvatarFallback(host, post) {
      if (!host) return;
      const img = host.querySelector('img.dd-syndicate-articles__author-avatar');
      if (!img) return;
      img.addEventListener(
        'error',
        () => {
          const fallback = document.createElement('span');
          fallback.className =
            'dd-syndicate-articles__author-avatar dd-syndicate-articles__author-avatar--fallback';
          fallback.setAttribute('aria-hidden', 'true');
          fallback.textContent = ((post && post.author) || '?').trim().charAt(0).toUpperCase();
          img.replaceWith(fallback);
        },
        { once: true }
      );
    }

    _postFooterHtml(post, prevPost, nextPost) {
      const topics = post.topics || [];
      const tagsHtml = topics.length
        ? topics
            .map(
              (t) =>
                `<a class="dd-syndicate-articles__tag" href="${this._escapeAttr(`${this.articlesPageUrl}?topic=${encodeURIComponent(t.toLowerCase())}`)}">${this._escape(t)}</a>`
            )
            .join('')
        : '';

      const tagsRow = tagsHtml
        ? `<div class="dd-syndicate-articles__tags-row">
            <span class="dd-syndicate-articles__tags-label">Tags:</span>
            <div class="dd-syndicate-articles__tags">${tagsHtml}</div>
          </div>`
        : '';

      const prevHtml = prevPost
        ? `<a class="dd-syndicate-articles__post-nav-link dd-syndicate-articles__post-nav-link--prev" href="${this._escapeAttr(`${this.articlesPageUrl}?slug=${encodeURIComponent(prevPost.slug)}`)}">
            <span class="dd-syndicate-articles__post-nav-label">
              ${DD_SA_LUCIDE['chevron-left']}
              Previous Post
            </span>
            <span class="dd-syndicate-articles__post-nav-title">${this._escape(prevPost.title)}</span>
          </a>`
        : '<div class="dd-syndicate-articles__post-nav-link dd-syndicate-articles__post-nav-link--empty"></div>';

      const nextHtml = nextPost
        ? `<a class="dd-syndicate-articles__post-nav-link dd-syndicate-articles__post-nav-link--next" href="${this._escapeAttr(`${this.articlesPageUrl}?slug=${encodeURIComponent(nextPost.slug)}`)}">
            <span class="dd-syndicate-articles__post-nav-label">
              Next Post
              ${DD_SA_LUCIDE['chevron-right']}
            </span>
            <span class="dd-syndicate-articles__post-nav-title">${this._escape(nextPost.title)}</span>
          </a>`
        : '<div class="dd-syndicate-articles__post-nav-link dd-syndicate-articles__post-nav-link--empty"></div>';

      if (!tagsRow && !prevPost && !nextPost) return '';

      return `
        <footer class="dd-syndicate-articles__post-footer">
          ${tagsRow}
          <div class="dd-syndicate-articles__post-nav">
            ${prevHtml}
            ${nextHtml}
          </div>
        </footer>`;
    }

    _paywallHtml() {
      const ctaHtml = this.customerLoggedIn
        ? this._paywallCtaHtml('order')
        : this._paywallCtaHtml('login');

      return `
        <div class="dd-syndicate-articles__paywall" role="region" aria-label="Unlock to read more">
          <div class="dd-syndicate-articles__paywall-card">
            <span class="dd-syndicate-articles__paywall-icon" aria-hidden="true">${DD_SA_LUCIDE.lock}</span>
            <p class="dd-syndicate-articles__paywall-title">Unlock to read more</p>
            <p class="dd-syndicate-articles__paywall-body">
              Syndicate articles are for Drop Dead pros who have placed at least one order.
            </p>
            ${ctaHtml}
          </div>
        </div>`;
    }

    _paywallCtaHtml(variant) {
      const tpl = this.querySelector(`template[data-dd-sa-paywall-cta="${variant}"]`);
      if (tpl && tpl.innerHTML.trim()) return tpl.innerHTML.trim();

      if (variant === 'order') {
        return `<a class="btn btn--primary btn--small btn--icon-solid dd-syndicate-articles__paywall-cta" href="${this._escapeAttr(this.syndicatePageUrl)}"><span class="btn__text">Unlock with your first order</span></a>`;
      }

      return `<a class="btn btn--primary btn--small btn--icon-solid dd-syndicate-articles__paywall-cta" href="/account/login" data-registration-overlay-open data-overlay-path="/login" rel="nofollow"><span class="btn__text">Log in to unlock</span></a>`;
    }

    _hydrateCircleVideos(root) {
      if (!root || !root.querySelectorAll) return;
      const videos = root.querySelectorAll('video.dd-sa-video[data-dd-sa-hls]');
      videos.forEach((video) => {
        if (video.dataset.ddSaHydrated === '1') return;
        const hlsUrl = video.getAttribute('data-dd-sa-hls');
        if (!hlsUrl) return;
        const native = video.canPlayType('application/vnd.apple.mpegurl');
        if (native && native !== 'no') {
          video.dataset.ddSaHydrated = '1';
          return;
        }
        video.dataset.ddSaHydrated = '1';
        this._attachHls(video, hlsUrl);
      });
    }

    _attachHls(video, src) {
      const start = () => {
        try {
          if (!window.Hls || !window.Hls.isSupported()) return;
          const hls = new window.Hls({ capLevelToPlayerSize: true });
          hls.loadSource(src);
          hls.attachMedia(video);
        } catch (err) {
          console.error('[dd-sa] hls attach failed', err);
        }
      };

      if (window.Hls) {
        start();
        return;
      }

      if (!window._ddSaHlsLoading) {
        window._ddSaHlsLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = HLS_SCRIPT_SRC;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('hls.js failed to load'));
          document.head.appendChild(script);
        }).catch((err) => {
          console.error('[dd-sa]', err);
        });
      }

      window._ddSaHlsLoading.then(start);
    }

    _sanitizeCircleHtml(html) {
      if (!html || typeof html !== 'string') return '';
      const trimmed = html.trim();
      if (!trimmed) return '';

      const blockedTags = new Set([
        'script', 'style', 'iframe', 'object', 'embed', 'svg', 'math',
        'link', 'meta', 'base', 'form', 'input', 'button', 'textarea',
        'select', 'option', 'template', 'frame', 'frameset', 'applet',
      ]);
      const urlAttrs = new Set(['href', 'src', 'xlink:href', 'formaction', 'poster', 'srcset']);
      const unsafeUrl = (value) => {
        const v = String(value || '').trim();
        if (!v) return true;
        if (/^(javascript|vbscript|data:text\/html)/i.test(v)) return true;
        return /^data:/i.test(v) && !/^data:image\//i.test(v);
      };

      const doc = new DOMParser().parseFromString(trimmed, 'text/html');
      const root = doc.createElement('div');
      root.id = 'dd-sa-sanitize-root';
      while (doc.body.firstChild) {
        root.appendChild(doc.body.firstChild);
      }
      if (!root.childNodes.length) return '';

      const walk = (node) => {
        [...node.children].forEach((el) => {
          const tag = el.tagName.toLowerCase();
          if (blockedTags.has(tag)) {
            el.remove();
            return;
          }

          [...el.attributes].forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (name.startsWith('on') || name === 'style') {
              el.removeAttribute(attr.name);
              return;
            }
            if (!urlAttrs.has(name)) return;

            if (name === 'srcset') {
              const safe = attr.value
                .split(',')
                .map((part) => {
                  const chunk = part.trim();
                  if (!chunk) return '';
                  const url = chunk.split(/\s+/)[0];
                  return unsafeUrl(url) ? '' : chunk;
                })
                .filter(Boolean)
                .join(', ');
              if (safe) el.setAttribute('srcset', safe);
              else el.removeAttribute('srcset');
              return;
            }

            if (unsafeUrl(attr.value)) el.removeAttribute(attr.name);
          });

          walk(el);
        });
      };

      walk(root);
      return root.innerHTML;
    }

    _prepareBodyHtml(html, heroSrc) {
      if (!html || typeof html !== 'string') return '';
      const trimmed = html.trim();
      if (!trimmed) return '';

      const doc = new DOMParser().parseFromString(
        `<div id="dd-sa-prose-root">${this._sanitizeCircleHtml(trimmed)}</div>`,
        'text/html'
      );
      const root = doc.getElementById('dd-sa-prose-root');
      if (!root) return trimmed;

      this._removeHeroDuplicate(root, heroSrc);
      this._enhanceProseImages(root, doc);
      this._enhanceProseBlocks(root);

      return root.innerHTML;
    }

    /* When the post has no cover image, the API falls back to the first
       body image for image_url. The hero already shows it, so remove
       that same image from the prose to avoid showing it twice. */
    _removeHeroDuplicate(root, heroSrc) {
      if (!heroSrc) return;
      const first = root.querySelector('img');
      if (!first || first.getAttribute('src') !== heroSrc) return;

      const link = first.parentElement?.tagName === 'A' ? first.parentElement : null;
      const mediaNode = link || first;
      const host = mediaNode.parentElement;
      mediaNode.remove();
      if (host && host !== root && !host.textContent.trim() && !host.querySelector('img')) {
        host.remove();
      }
    }

    _enhanceProseImages(root, doc) {
      root.querySelectorAll('figure').forEach((fig) => {
        fig.classList.add('dd-sa-figure');
        fig.querySelectorAll('img').forEach((image) => image.classList.add('dd-sa-figure__img'));
        const cap = fig.querySelector('figcaption');
        if (cap) cap.classList.add('dd-sa-figure__caption');
        if (fig.parentElement === root) fig.classList.add('dd-sa-figure--wide');
      });

      const imgs = [...root.querySelectorAll('img')];
      imgs.forEach((img) => {
        if (img.closest('figure.dd-sa-figure')) return;

        img.classList.add('dd-sa-figure__img');
        img.removeAttribute('width');
        img.removeAttribute('height');
        if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
        if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');

        const link = img.parentElement?.tagName === 'A' ? img.parentElement : null;
        const mediaNode = link || img;
        const host = mediaNode.parentElement;

        const figure = doc.createElement('figure');
        figure.className = 'dd-sa-figure';

        if (host && host.tagName === 'P' && this._paragraphIsSingleMedia(host)) {
          host.replaceWith(figure);
          figure.appendChild(mediaNode);
        } else if (host) {
          host.insertBefore(figure, mediaNode);
          figure.appendChild(mediaNode);
        }

        if (!figure.querySelector('figcaption')) {
          const alt = (img.getAttribute('alt') || '').trim();
          if (alt.length >= 4) {
            const cap = doc.createElement('figcaption');
            cap.className = 'dd-sa-figure__caption';
            cap.textContent = alt;
            figure.appendChild(cap);
          }
        }

        if (figure.parentElement === root) {
          figure.classList.add('dd-sa-figure--wide');
        }
      });
    }

    _paragraphIsSingleMedia(paragraph) {
      const nodes = [...paragraph.childNodes].filter((node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim().length > 0;
        return node.nodeType === Node.ELEMENT_NODE;
      });
      if (nodes.length !== 1) return false;
      const node = nodes[0];
      if (node.nodeName === 'IMG') return true;
      if (node.nodeName === 'A' && node.querySelector('img')) return true;
      return false;
    }

    _enhanceProseBlocks(root) {
      const firstP = root.querySelector(':scope > p');
      if (firstP) firstP.classList.add('dd-sa-lead');

      root.querySelectorAll('blockquote').forEach((bq) => {
        bq.classList.add('dd-sa-pullquote');
      });

      root.querySelectorAll('ul, ol').forEach((list) => {
        list.classList.add('dd-sa-list', list.tagName === 'OL' ? 'dd-sa-list--ol' : 'dd-sa-list--ul');
      });

      root.querySelectorAll('h2, h3, h4').forEach((heading) => {
        heading.classList.add('dd-sa-heading', `dd-sa-heading--${heading.tagName.toLowerCase()}`);
      });

      root.querySelectorAll('p').forEach((p) => {
        if (p.classList.contains('dd-sa-lead')) return;
        if (this._paragraphIsCallout(p)) p.classList.add('dd-sa-callout');
      });

      root.querySelectorAll('table').forEach((table) => {
        if (table.closest('.dd-sa-table-wrap')) return;
        const wrap = table.ownerDocument.createElement('div');
        wrap.className = 'dd-sa-table-wrap';
        table.classList.add('dd-sa-table');
        table.parentNode.insertBefore(wrap, table);
        wrap.appendChild(table);
      });

      root.querySelectorAll('iframe, video').forEach((media) => {
        if (media.closest('.dd-sa-embed')) return;
        const wrap = media.ownerDocument.createElement('div');
        wrap.className = 'dd-sa-embed';
        media.parentNode.insertBefore(wrap, media);
        wrap.appendChild(media);
      });

      root.querySelectorAll('a[href]').forEach((anchor) => {
        anchor.classList.add('dd-sa-link');
        const href = anchor.getAttribute('href') || '';
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
        try {
          const url = new URL(href, window.location.origin);
          if (url.hostname && url.hostname !== window.location.hostname) {
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer');
          }
        } catch (_err) {
          /* relative or malformed href */
        }
      });

      root.querySelectorAll('hr').forEach((hr) => hr.classList.add('dd-sa-divider'));

      root.querySelectorAll('pre').forEach((pre) => pre.classList.add('dd-sa-pre'));
      root.querySelectorAll('code').forEach((code) => {
        if (code.parentElement?.tagName !== 'PRE') code.classList.add('dd-sa-code');
      });
    }

    _paragraphIsCallout(paragraph) {
      const text = paragraph.textContent.trim();
      if (!text || text.length > 140) return false;
      const strong = paragraph.querySelector('strong, b');
      if (!strong) return false;
      return strong.textContent.trim() === text;
    }

    _authorTagsHtml(post) {
      const tags = Array.isArray(post && post.author_tags) ? post.author_tags.filter(Boolean) : [];
      if (!tags.length) return '';
      return `<span class="dd-syndicate-articles__member-tags">${tags
        .map((tag) => `<span class="dd-syndicate-articles__member-tag">${this._escape(tag)}</span>`)
        .join('')}</span>`;
    }

    _cardHtml(post) {
      const href = post.url || `${this.articlesPageUrl}?slug=${encodeURIComponent(post.slug)}`;
      const topic = (post.topics && post.topics[0]) || 'Article';
      const image = post.image_url
        ? `<img src="${this._escapeAttr(post.image_url)}" alt="" loading="lazy">`
        : `<div class="dd-syndicate-articles__media-fallback" aria-hidden="true"></div>`;
      const play = post.has_video
        ? `<span class="dd-syndicate-articles__play" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M8 5.14v13.72L19 12 8 5.14z"/></svg></span>`
        : '';

      return `
        <a href="${this._escapeAttr(href)}" class="dd-syndicate-articles__card">
          <div class="dd-syndicate-articles__media">
            <span class="dd-syndicate-articles__badge">${this._escape(topic)}</span>
            ${play}
            ${image}
          </div>
          <div class="dd-syndicate-articles__body">
            <h3 class="dd-syndicate-articles__title">${this._escape(post.title)}</h3>
            <p class="dd-syndicate-articles__meta">${this._formatDate(post.published_at)} · ${this._escape(post.author || '')}</p>
            ${this._authorTagsHtml(post)}
          </div>
        </a>`;
    }

    _skeletonCardCount() {
      if (this.limit > 0) return this._feedDisplayLimit();
      return 6;
    }

    _feedDisplayLimit() {
      if (this.limit <= 0) return 0;
      try {
        if (window.matchMedia('(min-width: 1400px)').matches) {
          return Math.min(this.limit, 4);
        }
        if (window.matchMedia('(min-width: 990px)').matches) {
          return Math.min(this.limit, 3);
        }
      } catch (_err) {
        /* matchMedia unavailable */
      }
      return Math.min(this.limit, 2);
    }

    _skeletonCardsHtml(count) {
      const total = count != null ? count : this._skeletonCardCount();
      return Array.from({ length: total }, (_, i) => `
        <div class="dd-syndicate-articles__card dd-syndicate-articles__card--skeleton" aria-hidden="true" style="--dd-sa-skel-delay:${(i * 0.08).toFixed(2)}s">
          <div class="dd-syndicate-articles__media">
            <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--badge"></span>
            <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--media"></span>
          </div>
          <div class="dd-syndicate-articles__body">
            <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--title"></span>
            <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--meta"></span>
          </div>
        </div>`).join('');
    }

    _skeletonFiltersHtml() {
      return `<div class="dd-syndicate-articles__filters dd-syndicate-articles__filters--skeleton" aria-hidden="true">
        ${[0, 1, 2, 3, 4].map((i) => `<span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--pill" style="--dd-sa-skel-delay:${(i * 0.06).toFixed(2)}s"></span>`).join('')}
      </div>`;
    }

    _detailSkeletonHtml() {
      const bodyLines = (widths) =>
        widths
          .map(
            (w, i) =>
              `<span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--body-line" style="width:${w};--dd-sa-skel-delay:${(i * 0.04).toFixed(2)}s"></span>`
          )
          .join('');

      return `
        <div class="dd-syndicate-articles__detail-skeleton" aria-hidden="true">
          <div class="dd-syndicate-articles__skel dd-syndicate-articles__skel--breadcrumb"></div>

          <header class="dd-syndicate-articles__article-hero dd-syndicate-articles__article-hero--skeleton">
            <div class="dd-syndicate-articles__article-hero-media" aria-hidden="true">
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--hero-media"></span>
            </div>
            <div class="dd-syndicate-articles__article-hero-content">
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--hero-pill"></span>
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--hero-title"></span>
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--hero-title dd-syndicate-articles__skel--hero-title-short"></span>
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--hero-meta"></span>
            </div>
          </header>

          <div class="dd-syndicate-articles__content dd-syndicate-articles__content--skeleton">
            <div class="dd-syndicate-articles__content-inner dd-sa-prose">
              <div class="dd-syndicate-articles__detail-skeleton-para">
                ${bodyLines(['100%', '100%', '96%', '100%', '88%'])}
              </div>
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--body-h2"></span>
              <div class="dd-syndicate-articles__detail-skeleton-para">
                ${bodyLines(['100%', '100%', '94%', '72%'])}
              </div>
              <div class="dd-syndicate-articles__detail-skeleton-para">
                ${bodyLines(['100%', '100%', '100%', '86%', '58%'])}
              </div>
            </div>
          </div>

          <footer class="dd-syndicate-articles__post-footer dd-syndicate-articles__post-footer--skeleton">
            <div class="dd-syndicate-articles__tags-row dd-syndicate-articles__tags-row--skeleton">
              <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--tags-label"></span>
              <div class="dd-syndicate-articles__tags">
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--tag-pill"></span>
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--tag-pill dd-syndicate-articles__skel--tag-pill-short"></span>
              </div>
            </div>
            <div class="dd-syndicate-articles__post-nav">
              <div class="dd-syndicate-articles__post-nav-skeleton">
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--nav-label"></span>
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--nav-title"></span>
              </div>
              <div class="dd-syndicate-articles__post-nav-skeleton dd-syndicate-articles__post-nav-skeleton--next">
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--nav-label"></span>
                <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--nav-title"></span>
              </div>
            </div>
          </footer>
        </div>`;
    }

    _detailRelatedSkeletonHtml() {
      return `
        <div class="dd-syndicate-articles__related dd-syndicate-articles__related--skeleton" aria-hidden="true">
          <span class="dd-syndicate-articles__skel dd-syndicate-articles__skel--related-heading"></span>
          <div class="dd-syndicate-articles__grid dd-syndicate-articles__grid--3 dd-syndicate-articles__grid--skeleton">
            ${this._skeletonCardsHtml(3)}
          </div>
        </div>`;
    }

    _showListSkeleton() {
      this._skeletonVisible = true;
      const filterHost = this.querySelector('[data-dd-sa-filters]');
      const gridHost = this.querySelector('[data-dd-sa-grid]');
      const gridClass =
        this.limit > 0
          ? 'dd-syndicate-articles__grid dd-syndicate-articles__grid--feed dd-syndicate-articles__grid--skeleton'
          : 'dd-syndicate-articles__grid dd-syndicate-articles__grid--skeleton';

      if (filterHost) {
        filterHost.innerHTML = this.limit > 0 ? '' : this._skeletonFiltersHtml();
      }
      if (gridHost) {
        gridHost.innerHTML = `<div class="${gridClass}">${this._skeletonCardsHtml()}</div>`;
      }
    }

    _showDetailSkeleton() {
      this._skeletonVisible = true;
      const host = this.querySelector('[data-dd-sa-detail]');
      if (host) host.innerHTML = this._detailSkeletonHtml();
      const relatedHost = this.querySelector('[data-dd-sa-related]');
      if (relatedHost) relatedHost.innerHTML = this._detailRelatedSkeletonHtml();
    }

    _hostHasSkeleton(host) {
      if (!host) return false;
      return !!host.querySelector(
        '.dd-syndicate-articles__card--skeleton, .dd-syndicate-articles__filters--skeleton, .dd-syndicate-articles__detail-skeleton'
      );
    }

    _prefersReducedMotion() {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (_err) {
        return false;
      }
    }

    _revealHost(host, html, opts = {}) {
      if (!host) return;

      const shouldAnimate =
        this._skeletonVisible && this._hostHasSkeleton(host) && !this._prefersReducedMotion();

      if (!shouldAnimate) {
        host.innerHTML = html;
        this._hydrateCircleVideos(host);
        return;
      }

      const duration = opts.kind === 'filters' ? 280 : 360;
      const outgoing = document.createElement('div');
      outgoing.className = 'dd-syndicate-articles__swap-out';
      while (host.firstChild) outgoing.appendChild(host.firstChild);

      const incoming = document.createElement('div');
      incoming.className = 'dd-syndicate-articles__swap-in is-pending';
      incoming.innerHTML = html;
      this._hydrateCircleVideos(incoming);

      host.appendChild(outgoing);
      host.appendChild(incoming);

      const lockHeight = Math.max(outgoing.offsetHeight, incoming.offsetHeight);
      if (lockHeight > 0) host.style.minHeight = `${lockHeight}px`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          outgoing.classList.add('is-leaving');
          incoming.classList.add('is-entering');
          incoming.classList.remove('is-pending');
        });
      });

      window.setTimeout(() => {
        outgoing.remove();
        while (incoming.firstChild) host.appendChild(incoming.firstChild);
        incoming.remove();
        host.style.minHeight = '';
        this._hydrateCircleVideos(host);
      }, duration);
    }

    _showListError(msg) {
      const gridHost = this.querySelector('[data-dd-sa-grid]');
      const filterHost = this.querySelector('[data-dd-sa-filters]');
      if (filterHost) filterHost.innerHTML = '';
      if (gridHost) {
        gridHost.innerHTML = `<p class="dd-syndicate-articles__empty dd-syndicate-articles__error" role="alert">${this._escape(msg)}</p>`;
      }
      this._setStatus(msg);
    }

    _showDetailError(msg) {
      const host = this.querySelector('[data-dd-sa-detail]');
      if (host) {
        host.innerHTML = `<p class="dd-syndicate-articles__empty dd-syndicate-articles__error" role="alert">${this._escape(msg)}</p>`;
      }
      const relatedHost = this.querySelector('[data-dd-sa-related]');
      if (relatedHost) relatedHost.innerHTML = '';
      this._setStatus(msg);
    }

    _readListCache() {
      try {
        const raw = sessionStorage.getItem(LIST_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || Date.now() - parsed.ts > LIST_CACHE_TTL_MS) return null;
        return parsed.data || null;
      } catch (_err) {
        return null;
      }
    }

    _writeListCache(data) {
      try {
        sessionStorage.setItem(
          LIST_CACHE_KEY,
          JSON.stringify({
            ts: Date.now(),
            data: { posts: data.posts || [], topics: data.topics || [] },
          })
        );
      } catch (_err) {
        /* quota / private mode */
      }
    }

    _setBusy(busy) {
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    _statusEl() {
      return (
        this.querySelector('[data-dd-sa-status]') ||
        this.closest('.dd-syndicate-articles')?.querySelector('[data-dd-sa-status]') ||
        null
      );
    }

    _setStatus(msg) {
      const el = this._statusEl();
      if (!el) return;
      el.textContent = msg;
      el.classList.toggle('dd-syndicate-articles__error', !!msg);
    }

    _clearStatus() {
      const el = this._statusEl();
      if (!el) return;
      el.textContent = '';
      el.classList.remove('dd-syndicate-articles__error');
    }

    _formatDate(value) {
      if (!value) return '';
      try {
        return new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(new Date(value));
      } catch (_err) {
        return '';
      }
    }

    _escape(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _escapeAttr(str) {
      return this._escape(str).replace(/'/g, '&#39;');
    }

    _escapeUrlAttr(url) {
      return String(url || '')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _readTitleSuffix() {
      const title = document.title || '';
      const idx = title.lastIndexOf(' \u2013 ');
      if (idx === -1) return '';
      return title.slice(idx);
    }

    _shopOrigin() {
      return window.location.origin || '';
    }

    _absoluteArticleUrl(slug) {
      return `${this._shopOrigin()}${this.articlesPageUrl}?slug=${encodeURIComponent(slug)}`;
    }

    _upsertManagedMeta(name, content) {
      if (!content) return;
      let el = document.head.querySelector(`meta[name="${name}"][data-dd-sa-managed]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('data-dd-sa-managed', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    _upsertManagedProperty(property, content) {
      if (!content) return;
      let el = document.head.querySelector(`meta[property="${property}"][data-dd-sa-managed]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        el.setAttribute('data-dd-sa-managed', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    _upsertManagedCanonical(href) {
      if (!href) return;
      let el = document.head.querySelector('link[rel="canonical"][data-dd-sa-managed]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        el.setAttribute('data-dd-sa-managed', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    }

    _upsertJsonLd(id, payload) {
      let el = document.head.querySelector(`script[data-dd-sa-schema="${id}"]`);
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-dd-sa-schema', id);
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(payload);
    }

    _removeJsonLd(id) {
      document.head.querySelector(`script[data-dd-sa-schema="${id}"]`)?.remove();
    }

    _seoDescriptionFromPost(post) {
      if (post.excerpt) return post.excerpt;
      const text = String(post.body_html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text.slice(0, 160);
    }

    _updateListSeo() {
      if (this._currentSlug()) return;

      const titles = this.posts.slice(0, 4).map((p) => p.title).filter(Boolean);
      const description =
        titles.length > 0
          ? `Strategy, pricing, and technique for extension artists: ${titles.join(', ')}.`
          : 'Strategy, pricing, and technique from the Drop Dead Syndicate team.';

      const canonical = `${this._shopOrigin()}${this.articlesPageUrl}`;
      this._upsertManagedMeta('description', description);
      this._upsertManagedProperty('og:type', 'website');
      this._upsertManagedProperty('og:title', document.title);
      this._upsertManagedProperty('og:description', description);
      this._upsertManagedProperty('og:url', canonical);
      this._upsertManagedProperty('twitter:card', 'summary');
      this._upsertManagedProperty('twitter:title', document.title);
      this._upsertManagedProperty('twitter:description', description);
      this._upsertManagedCanonical(canonical);
      this._removeJsonLd('article');
    }

    _updateDetailSeo(post, fullAccess) {
      if (!post) return;

      document.head.querySelector('[data-dd-sa-itemlist-schema]')?.remove();

      const headline = post.title || 'Syndicate article';
      document.title = `${headline}${this._titleSuffix}`;

      const description = this._seoDescriptionFromPost(post);
      const url = this._absoluteArticleUrl(post.slug);
      const image = post.image_url || '';

      this._upsertManagedMeta('description', description);
      this._upsertManagedProperty('og:type', 'article');
      this._upsertManagedProperty('og:title', headline);
      this._upsertManagedProperty('og:description', description);
      this._upsertManagedProperty('og:url', url);
      if (image) {
        this._upsertManagedProperty('og:image', image);
        this._upsertManagedProperty('twitter:card', 'summary_large_image');
        this._upsertManagedProperty('twitter:image', image);
      } else {
        this._upsertManagedProperty('twitter:card', 'summary');
      }
      this._upsertManagedProperty('twitter:title', headline);
      this._upsertManagedProperty('twitter:description', description);
      this._upsertManagedCanonical(url);

      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline,
        description,
        url,
        datePublished: post.published_at || undefined,
        author: post.author
          ? { '@type': 'Person', name: post.author }
          : undefined,
        image: image ? [image] : undefined,
        isAccessibleForFree: fullAccess === true,
        publisher: {
          '@type': 'Organization',
          name: document.querySelector('meta[property="og:site_name"]')?.content || 'Drop Dead Extensions',
        },
      };

      if (!fullAccess) {
        schema.hasPart = {
          '@type': 'WebPageElement',
          isAccessibleForFree: false,
          cssSelector: '.dd-syndicate-articles__content.is-locked',
        };
      }

      Object.keys(schema).forEach((key) => {
        if (schema[key] === undefined) delete schema[key];
      });

      this._upsertJsonLd('article', schema);
    }
  }

  customElements.define('dd-syndicate-articles', DDSyndicateArticles);
}
